import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import './profile/profilePanels.css';

const WINDOWS = [30, 60, 90, 180];

// Reporting surface for CustomFormBuilder responses (e.g. a monthly structured
// wellness check). Lists the gym's forms; for the selected one, shows response
// count + per-field summaries (numeric avg + distribution, or value frequency).
function freq(values) {
  const m = {};
  for (const v of values) { const k = String(v); m[k] = (m[k] || 0) + 1; }
  return Object.entries(m).map(([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count);
}

export default function CustomFormReports() {
  const [forms, setForms] = useState([]);
  const [formId, setFormId] = useState(null);
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get(`${API_BASE_URL}/api/v1/wellness/custom-forms`)
      .then(r => { if (!alive) return; const fs = Array.isArray(r.data) ? r.data : []; setForms(fs); setFormId(prev => prev ?? fs[0]?.id ?? null); })
      .catch(() => { if (alive) setForms([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!formId) { setData(null); return undefined; }
    let alive = true;
    api.get(`${API_BASE_URL}/api/v1/wellness/forms/${formId}/analytics?days=${days}`)
      .then(r => { if (alive) setData(r.data || null); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [formId, days]);

  const fields = data?.fields ? Object.values(data.fields) : [];
  const barMax = (arr) => Math.max(1, ...arr.map(x => x.count));

  return (
    <div className="pp-wrap" style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem 1rem 4rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
        <h1 style={{ fontSize: '1.4rem', margin: 0 }}>📋 Form Reports</h1>
        <Link to="/wellness/forms" style={{ marginLeft: 'auto', fontSize: '.85rem', color: '#4f46e5' }}>Build / edit forms →</Link>
      </div>
      <p style={{ color: '#6b7280', margin: '0 0 1rem', fontSize: '.9rem' }}>Responses to your custom forms (e.g. the monthly wellness check).</p>

      {loading ? <div className="pp-card">Loading…</div> : forms.length === 0 ? (
        <div className="pp-notice">No custom forms yet. <Link to="/wellness/forms">Build one →</Link> (e.g. a monthly wellness questionnaire), then responses show up here.</div>
      ) : (
        <>
          <div className="pp-toolbar" style={{ marginBottom: 14 }}>
            <select value={formId ?? ''} onChange={e => setFormId(Number(e.target.value))}
              style={{ padding: '.4rem .6rem', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '.9rem' }}>
              {forms.map(f => <option key={f.id} value={f.id}>{f.form_name || f.name || `Form ${f.id}`}</option>)}
            </select>
            <span style={{ marginLeft: 12 }} className="pp-toolbar-label">Window</span>
            {WINDOWS.map(w => <button key={w} className={`pp-chip ${days === w ? 'active' : ''}`} onClick={() => setDays(w)}>{w}d</button>)}
          </div>

          <div className="pp-tiles" style={{ gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: 14 }}>
            <div className="pp-tile"><span className="pp-tile-label">Responses</span><span className="pp-tile-value">{data?.response_count ?? '—'}</span></div>
            <div className="pp-tile"><span className="pp-tile-label">Athletes</span><span className="pp-tile-value">{data?.respondents ?? '—'}</span></div>
          </div>

          {fields.length === 0 ? (
            <div className="pp-notice">No responses in this window yet.</div>
          ) : fields.map((f, i) => {
            const nums = f.numerics || [];
            const isNumeric = nums.length > 0 && (f.field_type === 'number' || f.field_type === 'scale' || (f.values || []).length === 0);
            const avg = nums.length ? (nums.reduce((s, n) => s + n, 0) / nums.length) : null;
            const dist = isNumeric
              ? freq(nums.map(n => Math.round(n)))
              : freq(f.values || []);
            const max = barMax(dist);
            return (
              <div key={i} className="pp-card" style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                  <h3 style={{ margin: 0 }}>{f.label}</h3>
                  {isNumeric && avg != null && <span className="pp-sub">avg {avg.toFixed(1)} · {nums.length} answered</span>}
                  {!isNumeric && <span className="pp-sub">{(f.values || []).length} answered</span>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                  {dist.slice(0, 8).map((d, j) => (
                    <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 90, fontSize: '.82rem', color: '#475569', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.label}</span>
                      <div style={{ flex: 1, background: '#eef2ff', borderRadius: 6, height: 16 }}>
                        <div style={{ width: `${(d.count / max) * 100}%`, background: '#4f46e5', height: '100%', borderRadius: 6 }} />
                      </div>
                      <span style={{ width: 28, fontSize: '.8rem', color: '#6b7280', fontVariantNumeric: 'tabular-nums' }}>{d.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
